import type {
    Breadcrumb,
    CategoryInterface,
    Maybe
} from '@pylot-data/pylotschema'
import { ProductInterface, CategoryTree, CmsPage } from '@pylot-data/fwrdschema'
import { buildModelUrl, getFirstNotEmpty } from '@corratech/pylot-utils'
import { TFunction } from 'react-i18next'
import { ModelTypeEnum, PylotFrontendConfig } from 'config'

type GetBreadcrumbs = {
    product?: Maybe<ProductInterface>
    cmsPage?: Maybe<CmsPage>
    category?: Maybe<CategoryInterface>
    pathname: string
    t: TFunction<string[]>
    titleOverride?: string
    config: PylotFrontendConfig
}

export type BreadcrumbItem = {
    '@type': string
    position: number
    name: string
    item?: string
}

type BreadcrumbData = {
    name: string
    url_path: string
}

type CategoryWithChildren = CategoryInterface & {
    children?: Maybe<Array<Maybe<CategoryWithChildren>>>
}

const isCategoryWithChildren = (
    category?: Maybe<CategoryInterface>
): category is CategoryWithChildren => {
    return !!category && 'children' in category
}

// Convert Breadcrumb type signature to Category-alike signature
const convertBreadcrumbType = (
    items: Maybe<Array<Maybe<Breadcrumb>>>
): Array<Maybe<BreadcrumbData>> => {
    return items
        ? items.map((item) => {
              return {
                  name: String(item?.category_name),
                  url_path: String(item?.category_url_path)
              }
          })
        : []
}

const reduceCategories = (
    config: PylotFrontendConfig,
    result: Array<Maybe<BreadcrumbItem>>,
    suffix?: string | null,
    items?: Maybe<
        Array<Maybe<CategoryTree | BreadcrumbData | CategoryInterface>>
    >
): void => {
    if (!items || !Array.isArray(items)) {
        return
    }
    const accumulate = { maxLength: 0, blockAccumulation: false }

    items.forEach((category) => {
        if (!category) {
            return
        }
        const urlPath = category.url_path
        if (accumulate.blockAccumulation || !urlPath) {
            return accumulate
        }
        const subCategories = urlPath.split('/')

        if (subCategories.length > accumulate.maxLength) {
            accumulate.maxLength = subCategories.length
            result.push({
                '@type': 'ListItem',
                position: result.length + 1,
                name: String(category?.name),
                item: `${config.base.url.baseUrl}${
                    config.base.url.model.category.directory
                }/${urlPath}${String(suffix)}`
            })
        } else {
            accumulate.blockAccumulation = true
        }
    })
}

export const getBreadcrumbs = ({
    product,
    cmsPage,
    category,
    pathname,
    t,
    titleOverride,
    config
}: GetBreadcrumbs): Array<BreadcrumbItem> => {
    const { model, baseUrl } = config.base.url
    const breadcrumbItems: Array<BreadcrumbItem> = [
        {
            '@type': 'ListItem',
            position: 1,
            name: t('breadcrumbs|Homepage'),
            item: baseUrl
        }
    ]

    const initCurrent = (name?: Maybe<string>, suffix?: string | null) => {
        breadcrumbItems.push({
            '@type': 'ListItem',
            position: breadcrumbItems.length + 1,
            name: String(name)
        })
    }

    if (product) {
        reduceCategories(
            config,
            breadcrumbItems,
            model.category.suffix,
            product?.categories
        )
        initCurrent(product?.name, model.product.suffix)
    } else if (isCategoryWithChildren(category)) {
        const sortedCats = category?.breadcrumbs?.sort((a, b) => {
            return a?.category_level! - b?.category_level!
        })
        sortedCats?.map((item) => {
            breadcrumbItems.push({
                '@type': 'ListItem',
                position: breadcrumbItems.length + 1,
                name: String(item?.category_name),
                item: buildModelUrl(
                    ModelTypeEnum.CATEGORY,
                    item?.category_url_path || null,
                    config
                )
            })
        })

        initCurrent(category?.name, model.category.suffix)
    } else if (pathname !== '' && (cmsPage || titleOverride)) {
        initCurrent(
            getFirstNotEmpty([titleOverride, cmsPage?.title]),
            model.cmsPage.suffix
        )
    }

    return breadcrumbItems
}
